import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { latLng, latLngBounds, LatLng, Map as LMap } from 'leaflet';
import {
	MapComponent,
	LayerService,
	MapStatus,
	MapStatusService,
	metersPerPixel,
	scaleDenominator,
	formatScaleDenominator,
	NOMINAL_CSS_PX_PER_MM,
	zoomForScaleDenominator,
	MapCaptureTaintError,
	SCALE_PRESETS,
	pointIconUrl,
	pointLabelModel,
	snapshotLabelZoom,
	DrawingService,
	nimmKarteAuf,
} from '@luzula/luzula-lib';
import { ProjectMarkersService } from '../project-markers.service';

// ─── Feintuning Punktsymbole/-labels im Snapshot ───────────────────────────
// Alle Werte sind Anteile der Bildbreite (auflösungsunabhängig). Hier drehen,
// wenn Symbole/Labels größer/kleiner sollen.
/** Symbol-HÖHE (Breite folgt dem natürlichen Seitenverhältnis, wie auf der Karte). */
const POINT_SYMBOL_FRACTION = 0.019;
/** Top-Label-Schriftgröße (Nummer). Detail-Labels skalieren relativ dazu (s. u.). */
const POINT_LABEL_FRACTION = 0.017;
/** Detail-Labels relativ zum Top-Label (spiegelt das Größenverhältnis der Karte:
 *  Seiten-Labels ≈ fs-2, Bemerkung-Ersatz ≈ fs*0.7, HBF kleiner). */
const SIDE_LABEL_RATIO = 0.85; // Bodenform/Humusform/STFG
const SMALL_TOP_LABEL_RATIO = 0.7; // Bemerkung als Top-Label
const HBF_LABEL_RATIO = 0.7; // HBF unten

/** Ergebnis der Karten-Aufnahme (landet in den props des Karten-Elements). */
export interface MapSnapshotResult {
	src: string;
	center: { lat: number; lng: number };
	zoom: number;
	/** Aufgenommene Geländebreite/-höhe in Metern (für den exakten Druckmaßstab). */
	groundWidthM: number;
	groundHeightM: number;
	/** Gewählter Ziel-Druckmaßstab (1:S) oder null, wenn keiner gewählt wurde. */
	targetScale: number | null;
}

/**
 * Modal zum Rahmen + Aufnehmen eines Karten-Ausschnitts.
 *
 * Bettet die echte Karten-Komponente (`<bp-map>`) mit den aktuell sichtbaren
 * Layern des Projekts ein (wie bohrpunkt-map). Der Nutzer rahmt den Ausschnitt,
 * wählt optional einen festen Maßstab, und „Aufnehmen" friert die Ansicht via
 * `html2canvas` als Bild ein (Data-URL).
 *
 * HINWEIS: nur live im Browser sinnvoll (echte Leaflet-Karte) — nicht unit-getestet.
 */
@Component({
	selector: 'bp-map-snapshot-modal',
	standalone: true,
	imports: [CommonModule, MapComponent],
	templateUrl: './map-snapshot-modal.component.html',
	styleUrls: ['./map-snapshot-modal.component.css'],
})
export class MapSnapshotModalComponent {
	private activeModal = inject(NgbActiveModal);
	ls = inject(LayerService);
	projectMarkers = inject(ProjectMarkersService);
	private drawing = inject(DrawingService);

	/** localStorage-Schlüssel für den zuletzt gerahmten Ausschnitt. */
	private static readonly VIEW_KEY = 'druck-layout-snapshot-view';

	readonly presets = SCALE_PRESETS;
	/** Karte ist initialisiert (Leaflet bereit) — schaltet „Aufnehmen" frei. */
	readonly ready = signal(false);
	readonly scaleText = signal('–');
	readonly capturing = signal(false);
	readonly error = signal('');
	/** Gewählter Ziel-Druckmaßstab (1:S) — wird beim Aufnehmen exakt über die Element-Größe gesetzt. */
	readonly selectedScale = signal<number | null>(null);

	/** Seitenverhältnis (Breite/Höhe) des Ziel-Karten-Elements — vom Aufrufer gesetzt, formt die Vorschau. */
	targetAspect = 4 / 3;
	/** Größe des Ziel-Karten-Elements in mm — für maßstabsgenaues Rahmen (0 = unbekannt). */
	targetWidthMm = 0;
	targetHeightMm = 0;

	/** Start-Ansicht (Default Deutschland-Mitte; persistentBounds stellt die letzte Ansicht wieder her). */
	mapStatus: MapStatus = { center: latLng(51.163, 10.448), zoom: 6 };

	private map: LMap | null = null;
	private mss = inject(MapStatusService);

	/** Aktueller Ausschnitt der Haupt-Karte (alle-bohrpunkte), beim Öffnen abgegriffen. */
	readonly mainMapView = signal<{ center: LatLng; zoom: number } | null>(null);
	/** Vom Aufrufer gesetzt: beim Öffnen direkt auf die Haupt-Karten-Ansicht springen. */
	adoptOnOpen = false;
	/** Vom Aufrufer gesetzt: nach dem Laden automatisch aufnehmen (Schnell-Weg ohne Rahmen). */
	autoCapture = false;
	private autoCaptureStarted = false;

	constructor() {
		// Ausschnitt der Haupt-Karte EINMALIG beim Öffnen merken — bevor die Modal-Karte
		// (persistentBounds) den globalen Status selbst überschreibt. Das ReplaySubject
		// spielt synchron alle gepufferten Stände ab; der letzte ist der aktuelle.
		let latest: MapStatus | undefined;
		this.mss.globalMapState().subscribe((s) => { latest = s; }).unsubscribe();
		if (latest?.center && typeof latest.zoom === 'number') {
			this.mainMapView.set({ center: latLng(latest.center.lat, latest.center.lng), zoom: latest.zoom });
		}
	}

	get layers() {
		return this.ls.mapDef().layers;
	}

	onMapReady(map: LMap): void {
		this.map = map;
		this.ready.set(true);
		this.updateScale();
		map.on('moveend zoomend', () => this.updateScale());
		// Leaflet-in-Modal-Gotcha: Container hat beim Init oft noch nicht die endgültige
		// Größe → invalidieren. Dann die Start-Ansicht wählen: auf Wunsch die Haupt-Karte
		// übernehmen, sonst den zuletzt gerahmten Ausschnitt, sonst aufs Projektausmaß.
		setTimeout(() => {
			this.map?.invalidateSize();
			if (this.adoptOnOpen && this.mainMapView()) {
				this.adoptMainMapView();
			} else if (!this.restoreView()) {
				this.zoomToProject();
			}
			this.updateScale();
			// Schnell-Weg: nach dem Laden der Kacheln automatisch aufnehmen.
			if (this.autoCapture) this.captureWhenSettled();
		}, 250);
	}

	/** Übernimmt Center/Zoom der Haupt-Karte (alle-bohrpunkte) in die Vorschau. */
	adoptMainMapView(): void {
		const v = this.mainMapView();
		if (!this.map || !v) return;
		this.map.setView(v.center, v.zoom);
		this.updateScale();
	}

	/**
	 * Wartet, bis die Kacheln der aktuellen Ansicht geladen sind (kurze Stabilitäts-
	 * phase, max. ~6 s Fallback), und nimmt dann automatisch auf. Liest nur den
	 * Lade-Zähler des MapStatusService — keine zusätzlichen Server-Requests.
	 */
	private captureWhenSettled(attempt = 0): void {
		if (this.autoCaptureStarted && attempt === 0) return;
		this.autoCaptureStarted = true;
		const MAX_ATTEMPTS = 40; // ~6 s bei 150 ms
		const settled = this.mss.tilesLoading() === 0;
		if ((settled && attempt >= 3) || attempt >= MAX_ATTEMPTS) {
			this.capture();
			return;
		}
		setTimeout(() => this.captureWhenSettled(settled ? attempt + 1 : 0), 150);
	}

	/** Stellt den zuletzt gemerkten Ausschnitt wieder her. true bei Erfolg. */
	private restoreView(): boolean {
		if (!this.map) return false;
		try {
			const raw = localStorage.getItem(MapSnapshotModalComponent.VIEW_KEY);
			if (!raw) return false;
			const v = JSON.parse(raw) as { lat: number; lng: number; zoom: number };
			if (typeof v?.lat !== 'number' || typeof v?.lng !== 'number' || typeof v?.zoom !== 'number') return false;
			this.map.setView([v.lat, v.lng], v.zoom);
			return true;
		} catch {
			return false;
		}
	}

	/** Merkt sich den aktuellen Ausschnitt fürs nächste Öffnen. */
	private saveView(): void {
		if (!this.map) return;
		try {
			const c = this.map.getCenter();
			localStorage.setItem(
				MapSnapshotModalComponent.VIEW_KEY,
				JSON.stringify({ lat: c.lat, lng: c.lng, zoom: this.map.getZoom() }),
			);
		} catch {
			/* localStorage evtl. nicht verfügbar (Inkognito) */
		}
	}

	/** Zoomt auf das Projektausmaß = kombinierte Bounds aller Bereiche (für den Knopf). */
	zoomToProject(): void {
		if (!this.map) return;
		const regions = (this.ls.regions()?.regions ?? []) as { bounds?: import('leaflet').LatLngBounds }[];
		const withBounds = regions.filter((r) => r?.bounds);
		if (!withBounds.length) return;
		const first = withBounds[0].bounds!;
		const combined = latLngBounds(first.getSouthWest(), first.getNorthEast());
		for (const r of withBounds.slice(1)) combined.extend(r.bounds!);
		if (combined.isValid()) this.map.fitBounds(combined.pad(0.1));
	}

	/**
	 * Wählt einen Ziel-Druckmaßstab. Der EXAKTE Maßstab wird beim Aufnehmen über die
	 * Element-Größe gesetzt (Geländebreite ÷ Element-mm) — unabhängig vom Bildschirm-
	 * Zoom. Zur Vorschau wird die Karte grob auf die passende (ganze) Zoomstufe gesetzt;
	 * bewusst KEIN fraktionaler Zoom (Karte schnappt auf ganze Stufen → sonst NG0100).
	 */
	applyPreset(denom: number): void {
		this.selectedScale.set(denom);
		if (!this.map) return;
		const c = this.map.getCenter();
		if (this.targetWidthMm > 0 && this.targetHeightMm > 0) {
			// Genau die Geländefläche rahmen, die das Element bei 1:denom zeigt (mittig).
			// fitBounds zoomt so, dass die Fläche PASST → aufgenommen wird ≥ diese Fläche,
			// das Element zeigt sie dann maßstabsgetreu (gefüllt, mittig beschnitten).
			const halfWm = (this.targetWidthMm * denom) / 1000 / 2;
			const halfHm = (this.targetHeightMm * denom) / 1000 / 2;
			const dLat = halfHm / 111320;
			const dLng = halfWm / (111320 * Math.cos((c.lat * Math.PI) / 180));
			this.map.fitBounds(latLngBounds([c.lat - dLat, c.lng - dLng], [c.lat + dLat, c.lng + dLng]));
		} else {
			this.map.setZoom(Math.round(zoomForScaleDenominator(denom, c.lat)));
		}
		this.updateScale();
	}

	async capture(): Promise<void> {
		if (!this.map) return;
		this.capturing.set(true);
		this.error.set('');
		try {
			const map = this.map;
			// Der Ablauf steht in der Bibliothek (`nimmKarteAuf`): Kacheln aus dem
			// Kartenbaum uebernehmen, Linien und Flaechen frisch zeichnen,
			// Beschriftungen scharf nachziehen. bpweb steuert nur bei, was nur es
			// weiss: seine Punkte und deren Beschriftung, und die Zeichnungen, die
			// nicht an dieser Karte haengen.
			const aufnahme = await nimmKarteAuf({
				map,
				weitereVektoren: () => this.drawing.getDrawnVectorLayers(),
				punkteZeichnen: (ctx, karte, faktor) => this.drawPoints(ctx, faktor, karte),
			});
			this.saveView();
			const result: MapSnapshotResult = {
				src: aufnahme.src,
				center: aufnahme.center,
				zoom: aufnahme.zoom,
				groundWidthM: aufnahme.groundWidthM,
				groundHeightM: aufnahme.groundHeightM,
				targetScale: this.selectedScale(),
			};
			this.activeModal.close(result);
		} catch (e) {
			this.error.set(
				e instanceof MapCaptureTaintError
					? e.message
					: 'Die Karte konnte nicht aufgenommen werden.',
			);
		} finally {
			this.capturing.set(false);
		}
	}

	cancel(): void {
		this.saveView();
		this.activeModal.dismiss();
	}


	/**
	 * Zeichnet die Projekt-Punkte (Symbol + Beschriftung) selbst aufs Bild — Größe
	 * relativ zur Bildbreite (konsistent, nicht Leaflets feste Pixelgröße), volle
	 * Deckkraft.
	 *
	 * Die Beschriftung spiegelt EXAKT die Live-Karte: `pointLabelModel` (lib) liefert
	 * pro Punkt dasselbe Modell wie `PointRenderingService.updatePointLabelLayer` —
	 * Top-Label (Nummer bzw. Bemerkung-Ersatz), Seiten-Labels Bodenform (rot)/
	 * Humusform (grün)/STFG (rot) und HBF (unten), inkl. der Zoom-Schwellen (Detail-
	 * Labels erst ab Stufe 16). So werden auch Punktarten ohne Nummer (z.B. Typ 9999)
	 * über ihre detailLabels beschriftet — genau wie auf der Karte.
	 */
	private async drawPoints(ctx: CanvasRenderingContext2D, scale: number, map: LMap): Promise<void> {
		const markers = this.projectMarkers.markers();
		if (!markers.length) return;
		const symbolPx = ctx.canvas.width * POINT_SYMBOL_FRACTION;
		const fontPx = Math.max(8, ctx.canvas.width * POINT_LABEL_FRACTION);
		const sideFontPx = Math.max(8, fontPx * SIDE_LABEL_RATIO);
		const hbfFontPx = Math.max(8, fontPx * HBF_LABEL_RATIO);
		// Label-Sichtbarkeit im Druck eine Zoomstufe früher als auf der Karte, damit
		// die Beschriftungen auch bei kleinen Druckmaßstäben (z.B. 1:5000) mitkommen.
		// Die Live-Karte bleibt unberührt (nur diese Aufnahme nutzt den Boost).
		const zoom = snapshotLabelZoom(map.getZoom());

		// Symbole vorladen (eindeutige URLs)
		const cache = new Map<string, HTMLImageElement | null>();
		const urls = [...new Set(markers.map((m) => pointIconUrl({ svg: m.svg, type: m.type })))];
		await Promise.all(urls.map(async (u) => cache.set(u, await this.loadImage(u))));

		ctx.save();
		for (const m of markers) {
			const cp = map.latLngToContainerPoint(m.latLng);
			const x = cp.x * scale;
			const y = cp.y * scale;
			const img = cache.get(pointIconUrl({ svg: m.svg, type: m.type }));
			// Symbolmaße: HÖHE = symbolPx, BREITE aus dem natürlichen Seitenverhältnis
			// des Bildes — genau wie die Karte (height fest, width:auto). Verhindert das
			// Stauchen nicht-quadratischer Symbole (z.B. Pfeile Typ 8–13).
			const symH = symbolPx;
			let symW = symbolPx;
			if (img) {
				const ih = img.naturalHeight || img.height || 1;
				const iw = img.naturalWidth || img.width || ih;
				symW = symbolPx * (iw / ih);
				ctx.save();
				ctx.translate(x, y);
				if (m.rotation) ctx.rotate((m.rotation * Math.PI) / 180);
				ctx.drawImage(img, -symW / 2, -symH / 2, symW, symH);
				ctx.restore();
			}

			const label = pointLabelModel(m, zoom);
			if (!label) continue;
			const topEdge = y - symH / 2;

			// Top-Label: Nummer (fett) oder Bemerkung-Ersatz (kleiner) — zentriert über dem Symbol.
			if (label.topText) {
				const topFs = label.topTextSmall ? fontPx * SMALL_TOP_LABEL_RATIO : fontPx;
				ctx.font = `${label.topTextSmall ? '400' : 'bold'} ${topFs}px sans-serif`;
				ctx.textAlign = 'center';
				ctx.textBaseline = 'bottom';
				ctx.lineWidth = Math.max(1, topFs * 0.18);
				const ly = topEdge - topFs * 0.15;
				ctx.strokeStyle = '#ffffff';
				ctx.strokeText(label.topText, x, ly);
				ctx.fillStyle = '#333333';
				ctx.fillText(label.topText, x, ly);
			}

			// Seiten-Labels: Bodenform/Humusform/STFG rechts neben dem Symbol, gestapelt.
			if (label.sideTexts) {
				ctx.font = `500 ${sideFontPx}px sans-serif`;
				ctx.textAlign = 'left';
				ctx.textBaseline = 'top';
				ctx.lineWidth = Math.max(1, sideFontPx * 0.18);
				const sx = x + symW / 2 + sideFontPx * 0.3;
				let yOff = topEdge;
				for (const st of label.sideTexts) {
					ctx.strokeStyle = '#ffffff';
					ctx.strokeText(st.text, sx, yOff);
					ctx.fillStyle = st.color;
					ctx.fillText(st.text, sx, yOff);
					yOff += sideFontPx * 1.2;
				}
			}

			// HBF: unten-LINKS vom Punkt, rechtsbündig nach links auslaufend (wie die
			// Karte: textAlign right, knapp links der Punktmitte, unter dem Symbol).
			if (label.hbfText) {
				ctx.font = `400 ${hbfFontPx}px sans-serif`;
				ctx.textAlign = 'right';
				ctx.textBaseline = 'top';
				ctx.lineWidth = Math.max(1, hbfFontPx * 0.18);
				const hx = x - hbfFontPx * 0.3;
				const hy = y + symH / 2 + hbfFontPx * 0.1;
				ctx.strokeStyle = '#ffffff';
				ctx.strokeText(label.hbfText, hx, hy);
				ctx.fillStyle = '#333333';
				ctx.fillText(label.hbfText, hx, hy);
			}
		}
		ctx.restore();
	}

	/** Lädt ein Bild (Symbol-Asset oder data:-URL). null bei Fehler. */
	private loadImage(url: string): Promise<HTMLImageElement | null> {
		return new Promise((resolve) => {
			const img = new Image();
			img.onload = () => resolve(img);
			img.onerror = () => resolve(null);
			img.src = url;
		});
	}

	private updateScale(): void {
		if (!this.map) return;
		const mpp = metersPerPixel(this.map);
		this.scaleText.set(formatScaleDenominator(scaleDenominator(mpp, NOMINAL_CSS_PX_PER_MM)));
	}
}
